/* eslint-disable react/require-default-props */
/* eslint-disable react/no-unstable-nested-components */
import * as React from 'react';
import { Box, Button, InputLabel, Stack, Typography } from '@mui/material';
import {
  Configure,
  Hits,
  InstantSearch,
  useInstantSearch,
  useSearchBox,
} from 'react-instantsearch';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import { FormikErrors, useFormikContext } from 'formik';
import CloseIcon from '@mui/icons-material/HighlightOffRounded';
import { getCleanedPhone } from '../../utils/global/helper';
import { API_KEY, APP_ID, INDEX } from '../../algoliaconfig';
import { Appointment, GeneralRequest } from '../../interfaces';
import StyledPhoneInput from './PhoneInput';
import 'instantsearch.css/themes/satellite.css';
import { isCustomerEditDisabled } from '../../services/customers';
import useCalendarSelectedAppointment from '../../hooks/appointments/useCalendarSelectedAppointment';
import TextInput from './TextInput';
import { ClientSearchAutofillValues } from '../../utils/form/clientValues';
import useUser from '../../hooks/global/useUser';

const getInputName = (prefix: string[], name: string) =>
  `${prefix.join('.')}${prefix.length ? '.' : ''}${name}`;
const getValue = (values: any, prefix: string[], name: string) => {
  let value = values;
  // eslint-disable-next-line no-restricted-syntax
  for (const p of prefix ?? []) {
    value = value?.[p];
  }
  return value?.[name];
};

function Hit({
  hit,
  setFieldValue,
  setShowHits,
  validateForm,
  namePrefix = [],
}: {
  hit: any;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<ClientSearchAutofillValues>>;
  setShowHits: (show: boolean) => void;
  validateForm: () => Promise<FormikErrors<ClientSearchAutofillValues>>;
  namePrefix?: string[];
}) {
  const { setIndexUiState, refresh } = useInstantSearch();

  const handleClick = () => {
    const phone = hit.phoneNumber; // This could be a number or a string

    // Convert to string if it's not already a string
    const phoneString = typeof phone === 'string' ? phone : phone.toString();

    setFieldValue(getInputName(namePrefix, 'firstName'), hit.firstName);
    setFieldValue(getInputName(namePrefix, 'lastName'), hit.lastName);
    setFieldValue(getInputName(namePrefix, 'email'), hit.email);
    setFieldValue(getInputName(namePrefix, 'phoneNumber'), phoneString);
    setFieldValue(getInputName(namePrefix, 'customerId'), hit.customerId);

    setShowHits(false); // Hide hits after click

    // Refresh the algolia cache so new record will show up
    refresh();

    setIndexUiState(prevIndexUiState => ({
      ...prevIndexUiState,
      query: hit.fullName,
    }));

    setTimeout(() => validateForm(), 0);
  };

  return (
    <Box width="100%" onClick={handleClick}>
      <Typography variant="h5">{hit.fullName}</Typography>
      <Typography variant="body2">
        {getCleanedPhone(hit.phoneNumber?.toString() ?? '')}, {hit.email}
      </Typography>
    </Box>
  );
}

function AddClientBoundary({ children, handleCreateNewClient }: any) {
  const { indexUiState } = useInstantSearch();

  return (
    <>
      {children}
      <Box
        onClick={() => handleCreateNewClient(indexUiState.query || '')}
        className="create-client-hit">
        <Typography
          variant="body2"
          style={{
            color: 'var(--darkGrey)',
            paddingTop: 14,
            paddingBottom: 14,
            paddingLeft: 28,
          }}>
          Create new client &quot;
          <span style={{ fontWeight: 600 }}>{indexUiState.query}</span>&quot;
        </Typography>
      </Box>
    </>
  );
}

function CustomSearchBox({
  setShowHits,
  showHits,
  onReset,
}: {
  showHits: boolean;
  setShowHits: (show: boolean) => void;
  onReset: () => void;
}) {
  const { query, refine, clear } = useSearchBox();

  const handleReset = () => {
    clear();
    refine('');
    setShowHits(false); // Hide hits on reset
    onReset();
  };

  return (
    <Box
      position="relative"
      sx={{
        display: 'flex',
        flex: 1,
      }}>
      <input
        type="text"
        value={query}
        onChange={e => refine(e.target.value)}
        style={{
          flex: 1,
          flexGrow: 1,
        }}
        placeholder="Select or create client"
        onFocus={() => setShowHits(true)} // Show hits on focus
        autoComplete="off"
      />
      {(!!query || showHits) && (
        <Stack
          position="absolute"
          right={theme => theme.spacing(0.5)}
          height="100%"
          display="flex"
          justifyContent="center"
          zIndex={6}>
          <Button
            type="button"
            onClick={handleReset}
            sx={{ minWidth: 0, padding: 1 }}>
            <CloseIcon
              style={{
                color: 'var(--darkGrey)',
                fontSize: 20,
              }}
            />
          </Button>
        </Stack>
      )}
    </Box>
  );
}

const searchClient = algoliasearch(APP_ID, API_KEY);

interface Props {
  selectedRequest?: Appointment | GeneralRequest;
  allowCustomerWithoutAppointment?: boolean;
  title?: string;
  hideBirthday?: boolean;
  namePrefix?: string[];
  disableCustomerEdit?: boolean;
}

export default function ClientSearchAutofill({
  selectedRequest,
  allowCustomerWithoutAppointment,
  title = 'Client Information',
  hideBirthday = false,
  namePrefix = [],
  disableCustomerEdit: disableCustomerEditProp = false,
}: Props) {
  const { user: loadedUser } = useUser();
  const { values, setFieldValue, validateForm } =
    useFormikContext<ClientSearchAutofillValues>();
  const { selectedAppointment } = useCalendarSelectedAppointment();
  const [algoliaFilters, setAlgoliaFilters] = React.useState('');
  const [showHits, setShowHits] = React.useState(false);
  const [creatingNewClient, setCreatingNewClient] = React.useState(false);

  const customerIdValue = getValue(values, namePrefix, 'customerId');
  const firstNameValue = getValue(values, namePrefix, 'firstName');
  const customer = (selectedRequest ?? selectedAppointment)?.customer;
  const customerWithoutAppt =
    !!customerIdValue && allowCustomerWithoutAppointment && !customer
      ? {
          role: getValue(values, namePrefix, 'role') || 'CUSTOMER',
          phoneNumber: getValue(values, namePrefix, 'phoneNumber'),
        }
      : null;

  const showCutomerInputs =
    creatingNewClient ||
    selectedAppointment ||
    selectedRequest ||
    customerWithoutAppt;
  const disableCustomerEdit =
    (!!customer || !!customerWithoutAppt) &&
    (disableCustomerEditProp ||
      isCustomerEditDisabled((customer || customerWithoutAppt)!));

  React.useEffect(() => {
    const shopFilterString = `businessId:${loadedUser?.business.id}`;
    const artistFilterString = `employeeId:${loadedUser?.userId}`;

    if (!loadedUser?.canViewAllShopCustomers) {
      setAlgoliaFilters(artistFilterString);
    } else {
      setAlgoliaFilters(`${shopFilterString} OR ${artistFilterString}`);
    }
  }, [loadedUser]);

  // Function to handle the creation of a new client
  const handleCreateNewClient = (query: string) => {
    const names = query.split(' ');
    setFieldValue(getInputName(namePrefix, 'firstName'), names[0] || '');
    setFieldValue(
      getInputName(namePrefix, 'lastName'),
      names.length > 1 ? names.slice(1).join(' ') : '',
    );
    setShowHits(false); // Hide hits list
    setCreatingNewClient(true);
    setFieldValue(getInputName(namePrefix, 'email'), '');
    setFieldValue(getInputName(namePrefix, 'phoneNumber'), '+1');
    setFieldValue(getInputName(namePrefix, 'dob'), '');
  };

  const onClearCustomer = () => {
    setFieldValue(getInputName(namePrefix, 'customerId'), '');
    setFieldValue(getInputName(namePrefix, 'firstName'), '');
    setFieldValue(getInputName(namePrefix, 'lastName'), '');
    setFieldValue(getInputName(namePrefix, 'email'), '');
    setFieldValue(getInputName(namePrefix, 'phoneNumber'), '');
    setFieldValue(getInputName(namePrefix, 'dob'), '');
    setFieldValue(getInputName(namePrefix, 'role'), '');
    setCreatingNewClient(false);
  };

  return (
    <div className="input-group client-autofill">
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="h2">{title}</Typography>
        {showCutomerInputs && !!firstNameValue && !customer && (
          <Button
            sx={{
              minWidth: 0,
              p: 0.5,
            }}
            onClick={onClearCustomer}>
            <CloseIcon
              sx={{
                color: theme => theme.palette.medGrey.main,
                fontSize: 20,
              }}
            />
          </Button>
        )}
      </Stack>
      <div className="inputs">
        {!showCutomerInputs && (
          <div className="input-row">
            <div className="input">
              <InputLabel htmlFor="name">
                <Typography variant="h5">Name *</Typography>
              </InputLabel>
              <InstantSearch
                searchClient={searchClient}
                indexName={INDEX}
                insights>
                <Configure filters={algoliaFilters} distinct />
                <div style={{ flex: 1, position: 'relative' }}>
                  <CustomSearchBox
                    showHits={showHits}
                    setShowHits={setShowHits}
                    onReset={onClearCustomer}
                  />
                  {showHits && (
                    <AddClientBoundary
                      handleCreateNewClient={handleCreateNewClient}>
                      <Hits
                        hitComponent={hit => (
                          <Hit
                            hit={hit.hit}
                            setFieldValue={setFieldValue}
                            setShowHits={setShowHits}
                            validateForm={validateForm}
                            namePrefix={namePrefix}
                          />
                        )}
                      />
                    </AddClientBoundary>
                  )}
                </div>
              </InstantSearch>
            </div>
          </div>
        )}
        {showCutomerInputs && (
          <div className="input-row">
            <TextInput
              label="First Name *"
              name={getInputName(namePrefix, 'firstName')}
              disabled={disableCustomerEdit}
              placeholder="John"
              value={getValue(values, namePrefix, 'firstName')}
              onChange={e => {
                setFieldValue(
                  getInputName(namePrefix, 'firstName'),
                  e.target.value,
                );
              }}
            />
            <TextInput
              label="Last Name"
              name={getInputName(namePrefix, 'lastName')}
              disabled={disableCustomerEdit}
              placeholder="Doe"
              value={getValue(values, namePrefix, 'lastName')}
              onChange={e => {
                setFieldValue(
                  getInputName(namePrefix, 'lastName'),
                  e.target.value,
                );
              }}
            />
          </div>
        )}
        <div className="input-row">
          <TextInput
            label="Email"
            name={getInputName(namePrefix, 'email')}
            type="email"
            placeholder="Email"
            disabled={
              (!creatingNewClient && !values.firstName) || disableCustomerEdit
            }
            value={getValue(values, namePrefix, 'email')}
            onChange={e => {
              setFieldValue(getInputName(namePrefix, 'email'), e.target.value);
            }}
          />

          <StyledPhoneInput
            placeholder="Phone Number"
            name={getInputName(namePrefix, 'phoneNumber')}
            label="Phone *"
            disabled={
              (!creatingNewClient && !values.firstName) || disableCustomerEdit
            }
            search={false}
          />
        </div>
        {(creatingNewClient || selectedAppointment) && !hideBirthday && (
          <div className="input-row">
            <TextInput
              label="Birthday"
              name={getInputName(namePrefix, 'dob')}
              type="date"
              disabled={disableCustomerEdit}
              value={getValue(values, namePrefix, 'dob')}
              onChange={e => {
                setFieldValue(getInputName(namePrefix, 'dob'), e.target.value);
              }}
            />
          </div>
        )}
      </div>
      {disableCustomerEdit && (
        <Typography variant="caption" style={{ color: 'var(--darkGrey)' }}>
          This customer&apos;s information cannot be edited
        </Typography>
      )}
    </div>
  );
}
